/**
 * lazyWithRetry — React.lazy that survives chunk-load failures.
 *
 * Why: every page is a separate lazy chunk. When a tab loaded *before* a deploy
 * (or hitting a transient network blip) navigates, the browser asks for a chunk
 * hash that no longer exists at the production domain → "Failed to fetch
 * dynamically imported module". Plain React.lazy surfaces that straight to the
 * ErrorBoundary, which full-reloads — the visible "error, then it opens".
 *
 * This wrapper instead:
 *   1) retries the dynamic import a couple of times with a short backoff —
 *      transient blips recover silently, with NO reload and NO error flash;
 *   2) only if the retries still fail (almost always a stale chunk after a
 *      deploy) reloads ONCE to fetch the fresh index.html with valid hashes.
 *
 * sessionStorage guards against reload loops.
 */

import { lazy, type ComponentType } from 'react';

type LazyFactory<T extends ComponentType<Record<string, unknown>>> = () => Promise<{ default: T }>;

const RELOAD_KEY = 'buzzbit_chunk_reload';
const RETRY_ATTEMPTS = 2;
const RETRY_BASE_DELAY_MS = 400;

export function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  return (
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('error loading dynamically imported module') ||
    message.includes('Loading chunk') ||
    message.includes('Loading CSS chunk') ||
    message.includes('Unable to preload CSS')
  );
}

/** Reload once to pick up fresh chunk hashes; guarded against reload loops. */
export function reloadForStaleChunk(): boolean {
  const lastReload = sessionStorage.getItem(RELOAD_KEY);
  const now = Date.now();
  if (!lastReload || now - parseInt(lastReload, 10) > 10_000) {
    sessionStorage.setItem(RELOAD_KEY, String(now));
    window.location.reload();
    return true;
  }
  return false;
}

const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

export function lazyWithRetry<T extends ComponentType<Record<string, unknown>>>(
  factory: LazyFactory<T>,
): React.LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      return await factory();
    } catch (error) {
      if (!isChunkLoadError(error)) throw error;

      // 1) In-place retries — recover from a transient network blip without a reload.
      for (let attempt = 1; attempt <= RETRY_ATTEMPTS; attempt++) {
        await sleep(attempt * RETRY_BASE_DELAY_MS);
        try {
          return await factory();
        } catch {
          // keep retrying
        }
      }

      // 2) Still failing → stale chunk from an old deploy. Reload once.
      if (reloadForStaleChunk()) {
        // Never resolve while the page is reloading (prevents a flash of error UI).
        return new Promise<{ default: T }>(() => {});
      }

      // Already reloaded recently — surface to the ErrorBoundary as a last resort.
      throw error;
    }
  });
}
